import {
  Button,
  Clickable,
  Flex,
  Icons,
  Text,
  themeGet,
  View,
} from '@tabeo/scarf'
import Logo from 'components/Logo'
import PropTypes from 'prop-types'
import React from 'react'
import { Link, NavLink } from 'react-router'
import { useAuth } from 'resources/Auth'
import styled, { css } from 'styled-components'
import { Desktop, Mobile } from './Responsive'

const NavItem = styled(Clickable)`
  &.active {
    color: ${themeGet('colors.default.primary')};
  }
`

NavItem.defaultProps = {
  color: 'default.ink',
  fontWeight: 'regular',
}

function NavbarDesktop({ logout, token }) {
  return (
    <Flex width="100%" py={3}>
      <Link to="/">
        <Logo maxWidth="100px" maxHeight="40px" mr="40px" />
      </Link>
      {token && (
        <Flex>
          <NavItem as={NavLink} to="/tickets">
            Tickets
          </NavItem>
          <NavItem as={NavLink} to="/leads" ml="30px">
            Leads
          </NavItem>
          <NavItem as={NavLink} to="/bills" ml="30px">
            Bills
          </NavItem>
          <NavItem as={NavLink} to="/transactions" ml="30px">
            Transactions
          </NavItem>
          <NavItem as={NavLink} to="/merchants" ml="30px">
            Merchants
          </NavItem>
          <NavItem as={NavLink} to="/collection-accounts" ml="30px">
            Collections
          </NavItem>
          <Button
            ml="30px"
            sizeVariant="sm"
            leftIcon={props => <Icons.Plus light {...props} />}
            as={Link}
            to="?ticketId=new"
            replace
          >
            New ticket
          </Button>
          <NavItem
            onClick={logout}
            fontSize="16px"
            ml="30px"
            fontWeight="regular"
            color="default.ink"
          >
            Logout
          </NavItem>
        </Flex>
      )}
    </Flex>
  )
}

NavbarDesktop.propTypes = {
  logout: PropTypes.func.isRequired,
  token: PropTypes.string,
}

export const Hamburger = styled(View)`
  &:before,
  &,
  &:after {
    display: block;
    content: ' ';
    height: 3px;
    background: currentColor;
    transition: transform;
    transition-duration: 100ms;
    border-radius: 4px;
  }
  &:before {
    transform: translateY(-6px);
  }
  &:after {
    transform: translateY(3px);
  }
  ${p =>
    p.active &&
    css`
      & {
        background: transparent;
      }
      &:before {
        transform: translateY(0) rotate(45deg);
      }
      &:after {
        transform: translateY(-3px) rotate(-45deg);
      }
    `};
`

export const MobileNavItem = styled(Text)``

MobileNavItem.defaultProps = {
  cursor: 'pointer',
  px: 5,
  py: 5,
  borderBottom: 1,
  fontWeight: 'semibold',
  display: 'block',
}

function NavbarMobile({ logout, token }) {
  const [isOpen, toggle] = React.useState(false)
  React.useEffect(() => {
    function toggleMenu() {
      toggle(!isOpen)
    }
    if (isOpen) {
      window.addEventListener('click', toggleMenu)
      document.body.style.cursor = 'pointer'
    } else {
      document.body.style.cursor = 'unset'
    }
    return () => window.removeEventListener('click', toggleMenu)
  }, [isOpen, toggle])
  return (
    <>
      <Flex
        height="44px"
        px={4}
        bg={isOpen ? 'white' : 'transparent'}
        justifyContent={token ? 'space-between' : 'center'}
      >
        <Link to="/">
          <Logo maxHeight="26px" />
        </Link>
        {token && (
          <Hamburger
            color="grey.5"
            active={isOpen}
            onClick={() => toggle(!isOpen)}
            width="22px"
            mr={3}
          />
        )}
      </Flex>
      {isOpen && (
        <View
          borderTop={1}
          boxShadow={2}
          position="absolute"
          left="0"
          bg="white"
          zIndex="50"
          width="100%"
        >
          <MobileNavItem as={NavLink} to="/tickets">
            Tickets
          </MobileNavItem>
          <MobileNavItem as={NavLink} to="/leads">
            Leads
          </MobileNavItem>
          <MobileNavItem as={NavLink} to="/bills">
            Bills
          </MobileNavItem>
          <MobileNavItem as={NavLink} to="/transactions">
            Transactions
          </MobileNavItem>
          <MobileNavItem as={NavLink} to="/merchants">
            Merchants
          </MobileNavItem>
          <MobileNavItem as={NavLink} to="/collection-accounts">
            Collections
          </MobileNavItem>
          <MobileNavItem>
            <Button
              leftIcon={props => <Icons.Plus light {...props} />}
              as={Link}
              to="?ticketId=new"
              replace
            >
              New ticket
            </Button>
          </MobileNavItem>
          <MobileNavItem onClick={logout}>Logout</MobileNavItem>
        </View>
      )}
    </>
  )
}

NavbarMobile.propTypes = {
  logout: PropTypes.func.isRequired,
  token: PropTypes.string,
}

function Navbar() {
  const {
    data: { token },
    resource: { logout },
  } = useAuth()
  return (
    <View
      width="100%"
      position="sticky"
      style={{ zIndex: '500', top: 0 }}
      bg="white"
      boxShadow={1}
      className="no-print"
    >
      <View maxWidth="1440px" mx="auto" px={[0, 4]}>
        <Desktop>
          <NavbarDesktop logout={logout} token={token} />
        </Desktop>
        <Mobile>
          <NavbarMobile logout={logout} token={token} />
        </Mobile>
      </View>
    </View>
  )
}

export default Navbar
